import type { Directive } from "vue";

interface DebounceBinding {
  fn: () => void;
  event: string;
  time: number;
}

/**
 * 防止事件在短时间内被多次触发，使用防抖函数限制一定时间后触发
 * @example <input v-debounce="{fn: debounce, event: 'input', time: 5000}" />
 */
export default {
  mounted(el, binding) {
    const { fn, event, time } = binding.value as DebounceBinding;
    const _el = el as any;

    // 没绑定函数直接返回
    if (typeof fn !== "function") return;

    _el._timer = null;

    // 创建防抖处理函数
    const debounceHandler = () => {
      if (_el._timer !== null) {
        clearTimeout(_el._timer);
        _el._timer = null;
      }
      _el._timer = setTimeout(() => {
        fn();
      }, time);
    };

    // 保存处理函数引用和绑定信息，用于后续清理
    _el._debounceHandler = debounceHandler;
    _el._debounceBinding = { fn, event, time };

    // 监听指定事件
    el.addEventListener(event, debounceHandler);
  },

  unmounted(el) {
    const _el = el as any;

    // 清理定时器
    if (_el._timer !== null) {
      clearTimeout(_el._timer);
      _el._timer = null;
    }

    // 移除事件监听器
    if (_el._debounceHandler) {
      // 需要知道事件类型才能正确移除，这里可以从绑定值中获取
      const { event } = _el._debounceBinding || {};
      if (event) {
        el.removeEventListener(event, _el._debounceHandler);
      }
      _el._debounceHandler = null;
      _el._debounceBinding = null;
    }
  },

  updated(el, binding) {
    const { fn, event, time } = binding.value as DebounceBinding;
    const _el = el as any;

    // 如果绑定值发生变化，重新设置
    if (
      _el._debounceBinding &&
      (_el._debounceBinding.event !== event || _el._debounceBinding.time !== time || _el._debounceBinding.fn !== fn)
    ) {
      // 先清理旧的监听器
      if (_el._debounceHandler && _el._debounceBinding.event) {
        el.removeEventListener(_el._debounceBinding.event, _el._debounceHandler);
      }

      // 清理定时器
      if (_el._timer !== null) {
        clearTimeout(_el._timer);
        _el._timer = null;
      }

      // 重新设置
      if (typeof fn === "function") {
        const debounceHandler = () => {
          if (_el._timer !== null) {
            clearTimeout(_el._timer);
            _el._timer = null;
          }
          _el._timer = setTimeout(() => {
            fn();
          }, time);
        };

        _el._debounceHandler = debounceHandler;
        _el._debounceBinding = { fn, event, time };
        el.addEventListener(event, debounceHandler);
      }
    }
  },
} as Directive;
